import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BillingAPI } from '../api/billing';
import type {
  Subscription,
  Plan,
  Invoice,
  UsageData,
  BillingHookOptions,
  UsageMutationParams
} from '../types';

// 定数定義
const GRACE_PERIOD_DAYS = 7; // 支払い遅延からの猶予期間（日数）

// プラン一覧取得
export function usePlans() {
  return useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: async (): Promise<Plan[]> => {
      try {
        const result = await BillingAPI.getPlans();
        return result as Plan[];
      } catch (error) {
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5分間はキャッシュを使用
    retry: (failureCount, error) => {
      // ネットワーク関連エラーの場合は最大3回リトライ
      if (error.message.includes('ネットワーク接続エラー')) {
        return failureCount < 3;
      }
      // その他のエラーは1回のみリトライ
      return failureCount < 1;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

// サブスクリプション情報取得
export function useSubscription(options: BillingHookOptions = {}) {
  return useQuery<Subscription>({
    queryKey: ['subscription'],
    queryFn: BillingAPI.getUserSubscription,
    retry: (failureCount, error) => {
      // 認証エラーの場合はリトライしない
      if (
        error.message === 'Not authenticated' ||
        error.message.includes('認証が必要です')
      )
        return false;
      return failureCount < 3;
    },
    // 認証状態が確定するまで実行を遅延
    enabled: options.enabled !== false,
  });
}

// 利用状況取得
export function useUsage(
  startDate: string = '',
  endDate: string = '',
  usageType: string = '',
  options: BillingHookOptions = {}
) {
  return useQuery<UsageData>({
    queryKey: ['usage', startDate, endDate, usageType],
    queryFn: () => BillingAPI.getUsage(startDate, endDate, usageType),
    enabled: options.enabled !== false, // サブスクリプションがある場合のみ有効化したい場合は条件を追加
    retry: (failureCount, error) => {
      if (error.message === 'Not authenticated') return false;
      return failureCount < 3;
    },
  });
}

// 請求書一覧取得
export function useInvoices(options: BillingHookOptions = {}) {
  return useQuery<Invoice[]>({
    queryKey: ['invoices'],
    queryFn: async (): Promise<Invoice[]> => {
      try {
        const result = await BillingAPI.getInvoices();
        return result as Invoice[];
      } catch (error) {
        throw error;
      }
    },
    enabled: options.enabled !== false,
    retry: (failureCount, error) => {
      if (error.message === 'Not authenticated') return false;
      return failureCount < 3;
    },
  });
}

// 企業情報取得
export function useCompanyInfo() {
  return useQuery({
    queryKey: ['companyInfo'],
    queryFn: BillingAPI.getCompanyInfo,
  });
}

// Stripe Checkoutセッション作成
export function useCreateCheckoutSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: BillingAPI.createCheckoutSession,
    onSuccess: data => {
      // Stripe Checkoutページにリダイレクト
      window.location.href = data.url;
    },
    onError: error => {
    },
    // サブスクリプション関連のキャッシュを無効化（成功時に更新されるため）
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

// 利用量記録
export function useRecordUsage() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UsageMutationParams>({
    mutationFn: ({ usageType, quantity, metadata }) =>
      BillingAPI.recordUsage(usageType, quantity, metadata),
    onSuccess: () => {
      // 利用状況のキャッシュを無効化
      queryClient.invalidateQueries({ queryKey: ['usage'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: error => {
    },
  });
}

// 企業情報更新
export function useUpdateCompanyInfo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: BillingAPI.updateCompanyInfo,
    onSuccess: () => {
      // 企業情報とサブスクリプションのキャッシュを無効化
      queryClient.invalidateQueries({ queryKey: ['companyInfo'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: error => {
    },
  });
}

// サブスクリプションキャンセル
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: BillingAPI.cancelSubscription,
    onSuccess: () => {
      // サブスクリプション関連のキャッシュを無効化
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: error => {
    },
  });
}

// 請求書PDF ダウンロード
export function useDownloadInvoicePDF() {
  return useMutation({
    mutationFn: BillingAPI.downloadInvoicePDF,
    onError: error => {
    },
  });
}

// 複合フック: 課金関連の全データを取得
export function useBillingData() {
  const subscription = useSubscription();
  const plans = usePlans();
  const usage = useUsage('', '', ''); // デフォルト値を提供
  const invoices = useInvoices();

  return {
    subscription,
    plans,
    usage,
    invoices,
    isLoading:
      subscription.isLoading ||
      plans.isLoading ||
      usage.isLoading ||
      invoices.isLoading,
    error: subscription.error || plans.error || usage.error || invoices.error,
    refetch: () => {
      subscription.refetch();
      plans.refetch();
      usage.refetch();
      invoices.refetch();
    },
  };
}

// 課金状態の確認フック
export function useBillingStatus() {
  const { data: subscription, isLoading, isError } = useSubscription();

  // 共通のデフォルト状態オブジェクト
  const defaultBillingState = {
    subscription: null,
    isActive: false,
    isTrialing: false,
    isPastDue: false,
    isCanceled: false,
    trialInfo: null,
    planInfo: null,
    pastDueInfo: null,
    isReadOnly: false,
    isLoading: false,
    isError: false,
  };

  // フェイルセーフ用デフォルト（機能制限）
  const restrictedState = {
    ...defaultBillingState,
    canUseService: false,
    canPerformEvaluation: false,
    canUseAdminFeatures: false,
  };

  // トライアル用デフォルト（機能許可）
  const trialState = {
    ...defaultBillingState,
    canUseService: true,
    canPerformEvaluation: true,
    canUseAdminFeatures: true,
  };

  // ローディング中は機能を制限（フェイルセーフアプローチ）
  // UI側でローディングスピナーを表示する
  if (isLoading) {
    return {
      ...restrictedState,
      isLoading: true,
    };
  }

  // エラー時も機能を制限（フェイルセーフアプローチ）
  // 永続的なAPI障害時に無制限利用を防ぐ
  // React Queryのリトライ（3回）が全て失敗した場合のみここに到達
  if (isError) {
    return {
      ...restrictedState,
      isError: true,
    };
  }

  // サブスクリプション情報がない場合（新規ユーザー、レコード作成前など）
  // トライアル扱いとして利用可能にする（楽観的アプローチ）
  if (!subscription) {
    return {
      ...trialState,
      isTrialing: true, // サブスクリプションなし = トライアル扱い
    };
  }

  const isActive = subscription?.status === 'active';
  const isTrialing = subscription?.status === 'trialing';
  const isPastDue = subscription?.status === 'past_due';
  const isCanceled = subscription?.status === 'canceled';

  const trialInfo = subscription?.trial_end_date
    ? {
        endDate: new Date(subscription.trial_end_date),
        remainingEvaluations:
          subscription.trial_evaluation_count -
          (subscription.trial_evaluations_used || 0),
        isExpired: new Date() > new Date(subscription.trial_end_date),
      }
    : null;

  const planInfo = subscription?.plans
    ? {
        name: subscription.plans.name,
        monthlyFee: subscription.plans.monthly_fee,
        evaluationFee: subscription.plans.evaluation_fee,
        maxUsers: subscription.plans.max_users,
      }
    : null;

  // past_dueの場合の猶予期間計算
  const pastDueInfo = isPastDue && subscription?.current_period_end
    ? {
        periodEnd: new Date(subscription.current_period_end),
        daysRemaining: Math.max(0, Math.ceil((new Date(subscription.current_period_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24))),
        gracePeriodDays: GRACE_PERIOD_DAYS,
      }
    : null;

  // サービス利用可否の判定
  const canUseService = isActive || (isTrialing && !trialInfo?.isExpired);

  // 読み取り専用モード（past_due時）
  const isReadOnly = isPastDue;

  // 評価実行可否
  const canPerformEvaluation = canUseService && !isPastDue;

  // 管理機能使用可否（ユーザー追加、編集、バッチ処理など）
  const canUseAdminFeatures = canUseService && !isPastDue;

  return {
    subscription,
    isActive,
    isTrialing,
    isPastDue,
    isCanceled,
    trialInfo,
    planInfo,
    pastDueInfo,
    canUseService,
    isReadOnly,
    canPerformEvaluation,
    canUseAdminFeatures,
    isLoading: false,
    isError: false,
  };
}

// 利用制限チェックフック
export function useUsageLimits() {
  const { data: subscription } = useSubscription();
  const { data: usage } = useUsage('', '', ''); // デフォルト値を提供

  const currentUsage = usage?.summary?.assessment || 0;
  const isTrialing = subscription?.status === 'trialing';
  const trialLimit = subscription?.trial_evaluation_count || 0;
  const trialUsed = subscription?.trial_evaluations_used || 0;

  const remainingEvaluations = isTrialing ? trialLimit - trialUsed : null; // 有料プランは制限なし

  const canPerformEvaluation = isTrialing
    ? remainingEvaluations > 0
    : subscription?.status === 'active';

  return {
    currentUsage,
    remainingEvaluations,
    canPerformEvaluation,
    isTrialing,
    warningThreshold: isTrialing
      ? Math.max(1, Math.floor(trialLimit * 0.2))
      : null, // 20%残しで警告
    shouldShowWarning:
      isTrialing &&
      remainingEvaluations <= Math.max(1, Math.floor(trialLimit * 0.2)),
  };
}
